import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '../../atoms/button/button';
import IButton from '../../../models/button/IButton';

// Add these Modals configurations SASS variables to your global SASS file
// Refer: Line 7::Into.Discovery.BrowseWebMicrosites/src/styles/global.scss
// $modal-inner-padding: 1.5rem,
// $modal-backdrop-opacity: 0.6,
// $modal-header-border-width: 0rem,
// $modal-content-border-width: 0rem,
// $modal-footer-border-width: 0rem,
// $modal-footer-margin-between: 0rem

export interface ModalBoxProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footerButton?: IButton;
  modalSize?: 'sm' | 'lg' | 'xl';
  modalVerticallyCenter?: boolean;
  autoClickAfterDelay?: boolean;
  autoClickAfterDelayTimer?: number;
}

const ModalBox: FC<ModalBoxProps> = ({
  isOpen,
  title,
  onClose,
  children,
  footerButton,
  modalSize,
  modalVerticallyCenter,
  autoClickAfterDelay,
  autoClickAfterDelayTimer = 5000, // 5s
}) => {
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);
  const footerButtonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  const MIN_AUTO_CLICK_DELAY = 5000; // 5s
  const MAX_AUTO_CLICK_DELAY = 15000; // 15s

  const effectiveDelay =
    autoClickAfterDelayTimer && autoClickAfterDelayTimer >= MIN_AUTO_CLICK_DELAY
      ? autoClickAfterDelayTimer <= MAX_AUTO_CLICK_DELAY
        ? autoClickAfterDelayTimer // within range, use provided value
        : MAX_AUTO_CLICK_DELAY // thats too much, use max
      : MIN_AUTO_CLICK_DELAY; // uh oh thats too low, use min

  // Modal open/close transitions
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const timer = setTimeout(() => setShow(true), 100);
      return () => clearTimeout(timer);
    } else {
      setShow(false);
      const timer = setTimeout(() => setMounted(false), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Modal close on ESC key press
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Modal box auto click after delay and countdown timer
  // Use Case: Clavel Redirect Notification Modal
  useEffect(() => {
    let countdownTimer: NodeJS.Timeout;

    if (isOpen && autoClickAfterDelay) {
      setCountdown(effectiveDelay / 1000);

      countdownTimer = setInterval(() => {
        setCountdown((prev) => {
          if (prev && prev > 1) {
            return prev - 1;
          } else {
            clearInterval(countdownTimer);
            if (footerButtonRef.current) {
              footerButtonRef.current.click();
            }
            return 0;
          }
        });
      }, 1000); // Update countdown every second

      return () => clearInterval(countdownTimer);
    }

    return () => {};
  }, [isOpen, autoClickAfterDelay, effectiveDelay]);

  if (!mounted) return null;

  return createPortal(
    <>
      <div className={`modal-backdrop fade ${show ? 'show' : ''}`} onClick={onClose} />
      <div
        className={`modal fade d-block ${show ? 'show' : ''}`}
        tabIndex={-1}
        role='dialog'
        aria-modal='true'
        onClick={onClose}
      >
        <div
          className={`modal-dialog ${modalSize ? `modal-${modalSize}` : ''} ${modalVerticallyCenter ? 'modal-dialog-centered' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className='modal-content'>
            <div className='modal-header'>
              <h4 className='modal-title'>{title}</h4>
              <button type='button' className='btn-close' aria-label='Close' onClick={onClose} />
            </div>
            <div className='pb-0 pt-0 modal-body'>
              {children}
              {/* Display the countdown */}
              {autoClickAfterDelay &&
                countdown !== null &&
                ` This page will redirect in ${countdown} seconds.`}
            </div>
            {footerButton && (
              <div className='modal-footer'>
                <Button
                  buttonStyle={footerButton.buttonStyle}
                  buttonSize={footerButton.buttonSize}
                  buttonNewTab={footerButton.buttonNewTab}
                  buttonURL={footerButton.buttonURL}
                  buttonRole={footerButton.buttonRole}
                  copyColor={footerButton.copyColor}
                  backgroundColor={footerButton.backgroundColor}
                  hoverBackgroundColor={footerButton.hoverBackgroundColor}
                  activeBackgroundColor={footerButton.activeBackgroundColor}
                  buttonID={footerButton.buttonID}
                  buttonName={footerButton.buttonName}
                  buttonAriaLabel={footerButton.buttonAriaLabel}
                  onClick={onClose}
                  ref={footerButtonRef}
                >
                  {footerButton.children}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default ModalBox;
